import React, { useState, useEffect } from 'react';
import { QuestionService, QuestionData, QuestionVersionInput } from '../../services/QuestionService';
import { QuestionStatus, DataType, Category } from '../../types/enums';

interface QuestionCreationProps { onClose: () => void; onUpdate: () => void; }

const selectClass = "w-full px-3 py-2 border border-[#ccc] rounded text-sm focus:outline-none focus:border-[#4a90e2] transition-colors bg-white";
const textareaClass = "w-full px-3 py-2 border border-[#ccc] rounded text-sm focus:outline-none focus:border-[#4a90e2] transition-colors resize-y";
const labelClass = "font-semibold text-[#4a5568] text-sm mb-1 block";
const glowBtn: React.CSSProperties = { background: 'linear-gradient(120deg, #0b3d91, #1e68ff)', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 20px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 10px 26px rgba(14,52,161,0.35)', transition: 'transform 0.15s ease' };
const cancelBtn: React.CSSProperties = { background: 'linear-gradient(120deg, #f2f6ff, #e4ebff)', color: '#0c1a36', border: '1px solid #dfe7ff', borderRadius: '10px', padding: '10px 20px', fontWeight: 700, cursor: 'pointer' };

const QuestionCreation: React.FC<QuestionCreationProps> = ({ onClose, onUpdate }) => {
  const [newQuestion, setNewQuestion] = useState<QuestionData>({
    question: '', level: 1, status: QuestionStatus.CONCLUIDO, category: Category.DASHBOARD,
    vector_db_data: '', data_type: DataType.TEXT, additional_info: '', versions: []
  });
  const [newVersion, setNewVersion] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await QuestionService.createQuestion(newQuestion);
      onClose();
      onUpdate();
    } catch (err) { console.error('Error creating question:', err); setIsSubmitting(false); }
  };

  const handleAddVersion = () => {
    if (newVersion.trim()) {
      setNewQuestion(prev => ({ ...prev, versions: [...prev.versions, { version_question: newVersion.trim() }] }));
      setNewVersion('');
    }
  };
  const handleRemoveVersion = (index: number) => setNewQuestion(prev => ({ ...prev, versions: prev.versions.filter((_, i) => i !== index) }));
  const set = (key: string, value: unknown) => setNewQuestion(prev => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="bg-white rounded-xl w-[90%] max-w-xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h3 className="m-0 text-xl font-bold text-[#2d3748]">Nova Questão</h3>
          <button className="bg-transparent border-none text-2xl cursor-pointer text-[#718096] hover:text-[#2d3748] transition-colors" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          <div><label className={labelClass} htmlFor="qc-question">Pergunta:</label>
            <textarea id="qc-question" className={textareaClass} value={newQuestion.question} onChange={e => set('question', e.target.value)} placeholder="Digite a pergunta..." rows={2} required disabled={isSubmitting} />
          </div>
          <div><label className={labelClass} htmlFor="qc-level">Nível:</label>
            <select id="qc-level" className={selectClass} value={newQuestion.level} onChange={e => set('level', parseInt(e.target.value))} required disabled={isSubmitting}>
              <option value="1">1</option><option value="2">2</option><option value="3">3</option>
            </select>
          </div>
          <div><label className={labelClass} htmlFor="qc-status">Status:</label>
            <select id="qc-status" className={selectClass} value={newQuestion.status} onChange={e => set('status', e.target.value)} required disabled={isSubmitting}>
              {Object.values(QuestionStatus).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><label className={labelClass} htmlFor="qc-category">Categoria:</label>
            <select id="qc-category" className={selectClass} value={newQuestion.category} onChange={e => set('category', e.target.value)} required disabled={isSubmitting}>
              {Object.values(Category).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className={labelClass} htmlFor="qc-data-type">Tipo de Dado:</label>
            <select id="qc-data-type" className={selectClass} value={newQuestion.data_type} onChange={e => set('data_type', e.target.value)} required disabled={isSubmitting}>
              {Object.values(DataType).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Versions */}
          <div>
            <label className={labelClass}>Versões da Questão:</label>
            {newQuestion.versions.map((v, i) => (
              <div key={i} className="flex items-start justify-between gap-2 p-3 mb-2 bg-[#f8f9fa] border border-[#e2e8f0] rounded-lg">
                <div className="flex flex-col gap-1 flex-1">
                  <span className="text-xs font-bold text-[#6c757d]">Versão {i + 1}</span>
                  <p className="m-0 text-sm text-[#333]">{v.version_question}</p>
                </div>
                <button type="button" className="text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1 text-xs cursor-pointer hover:bg-red-100 transition-colors" onClick={() => handleRemoveVersion(i)} disabled={isSubmitting}>×</button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <textarea className={textareaClass + " flex-1"} value={newVersion} onChange={e => setNewVersion(e.target.value)} placeholder="Digite o conteúdo da nova versão..." rows={2} disabled={isSubmitting} />
              <button type="button" className="bg-[#4a90e2] text-white border-none rounded px-4 py-2 font-medium cursor-pointer hover:bg-[#357abd] transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed" onClick={handleAddVersion} disabled={!newVersion.trim() || isSubmitting}>+ Versão</button>
            </div>
          </div>

          <div><label className={labelClass} htmlFor="qc-vector">Dados do Banco de Vetores:</label>
            <textarea id="qc-vector" className={textareaClass} value={newQuestion.vector_db_data} onChange={e => set('vector_db_data', e.target.value)} disabled={isSubmitting} placeholder="Digite os dados do banco de vetores..." rows={2} />
          </div>
          <div><label className={labelClass} htmlFor="qc-info">Informações Adicionais:</label>
            <textarea id="qc-info" className={textareaClass} value={newQuestion.additional_info} onChange={e => set('additional_info', e.target.value)} disabled={isSubmitting} placeholder="Digite informações adicionais..." rows={2} />
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <button type="button" style={cancelBtn} onClick={onClose} disabled={isSubmitting}>Cancelar</button>
            <button type="submit" style={glowBtn} disabled={isSubmitting} className="disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none">{isSubmitting ? 'Criando...' : 'Criar Questão'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionCreation;